// ============================================================================
// CYBERGRID ASSAULT - Main Entry Point
// ============================================================================

import { Game, GameSettings } from '@/game/Game';
import { NetworkManager } from '@/network/NetworkManager';
import { FIREBASE_CONFIG } from '@/config/firebase.config';
import { PacketType } from '@/shared/types';
import { MAP_REGISTRY } from '@/game/map/GameMap';
import { AnalyticsService } from '@/services/AnalyticsService';
import { PlayerStatsService } from '@/services/PlayerStatsService';
import { LobbyBrowserService, PublicLobby } from '@/services/LobbyBrowserService';
import { VoiceChatService } from '@/services/VoiceChatService';
import { getGameAudio } from '@/game/GameAudio';

// ============================================================================
// UI Audio System (standalone for menu/lobby sounds)
// ============================================================================
class UIAudio {
  private context: AudioContext | null = null;
  private sounds = new Map<string, AudioBuffer>();
  private initialized = false;
  private loading = false;
  private enabled = true;
  private volume = 0.7; // Default UI volume

  async init(): Promise<void> {
    if (this.initialized || this.loading) return;
    this.loading = true;
    try {
      this.context = new AudioContext();
      // Load UI sounds
      await Promise.all([
        this.loadSound('hover', './assets/audio/ui/hover.mp3'),
        this.loadSound('click', './assets/audio/ui/click.mp3'),
      ]);
      this.initialized = true;
      console.log('[UIAudio] Initialized with', this.sounds.size, 'sounds');
    } catch (e) {
      console.warn('[UIAudio] Failed to initialize:', e);
    }
    this.loading = false;
  }

  private async loadSound(key: string, url: string): Promise<void> {
    if (!this.context) return;
    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
      this.sounds.set(key, audioBuffer);
      console.log(`[UIAudio] Loaded: ${key}`);
    } catch (e) {
      console.warn(`[UIAudio] Failed to load ${key}:`, e);
    }
  }

  play(key: string, volume = 0.5): void {
    if (!this.enabled || !this.context || !this.sounds.has(key)) {
      // Try to init if not ready
      if (!this.initialized && !this.loading) {
        this.init();
      }
      return;
    }
    // Resume context if suspended (browser autoplay policy)
    if (this.context.state === 'suspended') {
      this.context.resume();
    }
    const buffer = this.sounds.get(key)!;
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    const gainNode = this.context.createGain();
    gainNode.gain.value = volume * this.volume;
    source.connect(gainNode);
    gainNode.connect(this.context.destination);
    source.start(0);
  }

  playHover(): void {
    this.play('hover', 0.3);
  }

  playClick(): void {
    this.play('click', 0.5);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }
  
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }
}

const uiAudio = new UIAudio();

// ============================================================================
// Music Player System
// ============================================================================
class MusicPlayer {
  private context: AudioContext | null = null;
  private currentSource: AudioBufferSourceNode | null = null;
  private currentGain: GainNode | null = null;
  private currentTrack: string | null = null;
  private tracks = new Map<string, AudioBuffer>();
  private volume = 0.4;
  private isPaused = false;
  private pausedAt = 0;
  private startedAt = 0;

  private readonly TRACK_FILES: Record<string, string> = {
    neon_dreams: './assets/audio/music/neon_dreams.mp3',
    digital_pulse: './assets/audio/music/digital_pulse.mp3',
    cyber_rush: './assets/audio/music/cyber_rush.mp3',
    electric_horizon: './assets/audio/music/electric_horizon.mp3',
  };

  async init(): Promise<void> {
    if (!this.context) {
      this.context = new AudioContext();
    }
  }

  async loadTrack(trackId: string): Promise<void> {
    if (!this.context || this.tracks.has(trackId)) return;

    const url = this.TRACK_FILES[trackId];
    if (!url) return;

    try {
      const response = await fetch(url);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.context.decodeAudioData(arrayBuffer);
      this.tracks.set(trackId, audioBuffer);
    } catch (e) {
      console.warn(`Failed to load music track: ${trackId}`, e);
    }
  }

  async play(trackId: string): Promise<void> {
    if (!this.context) {
      await this.init();
    }

    // Stop current track if playing
    this.stop();

    // Load track if not already loaded
    if (!this.tracks.has(trackId)) {
      await this.loadTrack(trackId);
    }

    const buffer = this.tracks.get(trackId);
    if (!buffer || !this.context) return;

    // Create and configure audio nodes
    this.currentSource = this.context.createBufferSource();
    this.currentSource.buffer = buffer;
    this.currentSource.loop = true; // Loop the music
    this.currentSource.loopStart = 0;
    this.currentSource.loopEnd = buffer.duration;

    this.currentGain = this.context.createGain();
    this.currentGain.gain.value = this.volume;

    // Connect nodes
    this.currentSource.connect(this.currentGain);
    this.currentGain.connect(this.context.destination);

    // Start playback
    this.currentSource.start(0);
    this.currentTrack = trackId;
    this.startedAt = this.context.currentTime;
    this.pausedAt = 0;
    this.isPaused = false;
  }

  pause(): void {
    if (!this.currentSource || !this.context || this.isPaused) return;

    try {
      this.pausedAt = this.context.currentTime - this.startedAt;
      this.currentSource.stop();
      this.isPaused = true;
    } catch (e) {
      console.warn('Failed to pause music:', e);
    }
  }

  async resume(): Promise<void> {
    if (!this.isPaused || !this.currentTrack || !this.context) return;

    const buffer = this.tracks.get(this.currentTrack);
    if (!buffer) return;

    // Create new source node
    this.currentSource = this.context.createBufferSource();
    this.currentSource.buffer = buffer;
    this.currentSource.loop = true;
    this.currentSource.loopStart = 0;
    this.currentSource.loopEnd = buffer.duration;

    this.currentGain = this.context.createGain();
    this.currentGain.gain.value = this.volume;

    this.currentSource.connect(this.currentGain);
    this.currentGain.connect(this.context.destination);

    // Resume from paused position
    this.currentSource.start(0, this.pausedAt % buffer.duration);
    this.startedAt = this.context.currentTime - this.pausedAt;
    this.isPaused = false;
  }

  stop(): void {
    if (this.currentSource) {
      try {
        this.currentSource.stop();
      } catch (e) {
        // Ignore errors (source may already be stopped)
      }
      this.currentSource = null;
      this.currentGain = null;
      this.currentTrack = null;
      this.isPaused = false;
      this.pausedAt = 0;
      this.startedAt = 0;
    }
  }

  getCurrentTrack(): string | null {
    return this.currentTrack;
  }

  isPausedState(): boolean {
    return this.isPaused;
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.currentGain) {
      this.currentGain.gain.value = this.volume;
    }
  }
}

const musicPlayer = new MusicPlayer();

// Initialize UI audio on first user interaction (for browser autoplay policy)
const initUIAudio = () => {
  uiAudio.init();
  musicPlayer.init();
  document.removeEventListener('click', initUIAudio);
  document.removeEventListener('keydown', initUIAudio);
  document.removeEventListener('mousemove', initUIAudio);
};
document.addEventListener('click', initUIAudio);
document.addEventListener('keydown', initUIAudio);
document.addEventListener('mousemove', initUIAudio); // Init on mouse move so sounds are ready for hover

// Network manager for lobby (before game starts)
let lobbyNetwork: NetworkManager | null = null;

// Version from package.json (injected by Vite)
declare const __APP_VERSION__: string;

// DOM Elements
const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
const connectionUI = document.getElementById('connection-ui') as HTMLDivElement;
const versionBadge = document.getElementById('version-badge') as HTMLDivElement;
const playerNameInput = document.getElementById('player-name') as HTMLInputElement;
const hostButton = document.getElementById('btn-host') as HTMLButtonElement;
const joinButton = document.getElementById('btn-join') as HTMLButtonElement;
const roomCodeInput = document.getElementById('room-code') as HTMLInputElement;
const connectButton = document.getElementById('btn-connect') as HTMLButtonElement;
const backButton = document.getElementById('btn-back') as HTMLButtonElement;
const joinButtons = document.getElementById('join-buttons') as HTMLDivElement;

// Game Setup Settings Elements (main menu)
const settingsButton = document.getElementById('btn-settings') as HTMLButtonElement;
const settingsPanel = document.getElementById('settings-panel') as HTMLDivElement;
const creditsButton = document.getElementById('btn-credits') as HTMLButtonElement;
const creditsModal = document.getElementById('credits-modal') as HTMLDivElement;
const creditsCloseButton = document.getElementById('btn-credits-close') as HTMLButtonElement;
const howToPlayButton = document.getElementById('btn-how-to-play') as HTMLButtonElement;
const howToPlayModal = document.getElementById('how-to-play-modal') as HTMLDivElement;
const howToPlayCloseButton = document.getElementById('btn-how-to-play-close') as HTMLButtonElement;
const statsButton = document.getElementById('btn-stats') as HTMLButtonElement;
const statsModal = document.getElementById('stats-modal') as HTMLDivElement;
const statsCloseButton = document.getElementById('btn-stats-close') as HTMLButtonElement;
const micToggleButton = document.getElementById('btn-mic-toggle') as HTMLButtonElement;
const botCountSlider = document.getElementById('setting-bot-count') as HTMLInputElement;
const botCountValue = document.getElementById('bot-count-value') as HTMLSpanElement;
const botDifficultySelect = document.getElementById('setting-bot-difficulty') as HTMLSelectElement;
const friendlyBotsSlider = document.getElementById('setting-friendly-bots') as HTMLInputElement;
const friendlyBotsValue = document.getElementById('friendly-bot-count-value') as HTMLSpanElement;
const aiModeSelect = document.getElementById('setting-ai-mode') as HTMLSelectElement;
const gameModeSelect = document.getElementById('setting-game-mode') as HTMLSelectElement;
const mapSelect = document.getElementById('setting-map') as HTMLSelectElement;
const mapHint = document.getElementById('map-hint') as HTMLSpanElement;
const heroSelect = document.getElementById('setting-hero') as HTMLSelectElement;
const abilityQSelect = document.getElementById('setting-ability-q') as HTMLSelectElement;
const abilityFSelect = document.getElementById('setting-ability-f') as HTMLSelectElement;
const abilityQName = document.getElementById('ability-q-name') as HTMLSpanElement;
const abilityFName = document.getElementById('ability-f-name') as HTMLSpanElement;
const settingsSaveButton = document.getElementById('btn-settings-save') as HTMLButtonElement;
const settingsCloseButton = document.getElementById('btn-settings-close') as HTMLButtonElement;

// Audio Settings Elements (in settings panel - main menu)
const settingsAudioMasterVolume = document.getElementById('settings-audio-master-volume') as HTMLInputElement;
const settingsAudioMasterEnabled = document.getElementById('settings-audio-master-enabled') as HTMLInputElement;
const settingsAudioMasterValue = document.getElementById('settings-audio-master-value') as HTMLSpanElement;
const settingsAudioMusicVolume = document.getElementById('settings-audio-music-volume') as HTMLInputElement;
const settingsAudioMusicEnabled = document.getElementById('settings-audio-music-enabled') as HTMLInputElement;
const settingsAudioMusicValue = document.getElementById('settings-audio-music-value') as HTMLSpanElement;
const settingsAudioSfxVolume = document.getElementById('settings-audio-sfx-volume') as HTMLInputElement;
const settingsAudioSfxEnabled = document.getElementById('settings-audio-sfx-enabled') as HTMLInputElement;
const settingsAudioSfxValue = document.getElementById('settings-audio-sfx-value') as HTMLSpanElement;
const settingsAudioVoiceVolume = document.getElementById('settings-audio-voice-volume') as HTMLInputElement;
const settingsAudioVoiceEnabled = document.getElementById('settings-audio-voice-enabled') as HTMLInputElement;
const settingsAudioVoiceValue = document.getElementById('settings-audio-voice-value') as HTMLSpanElement;
const settingsAudioUiVolume = document.getElementById('settings-audio-ui-volume') as HTMLInputElement;
const settingsAudioUiEnabled = document.getElementById('settings-audio-ui-enabled') as HTMLInputElement;
const settingsAudioUiValue = document.getElementById('settings-audio-ui-value') as HTMLSpanElement;

// Pause Menu Elements (in-game)
const pauseMenu = document.getElementById('pause-menu') as HTMLDivElement;
const resumeButton = document.getElementById('btn-resume') as HTMLButtonElement;
const quitButton = document.getElementById('btn-quit') as HTMLButtonElement;
const sensitivitySlider = document.getElementById('setting-sensitivity') as HTMLInputElement;
const sensitivityValue = document.getElementById('sensitivity-value') as HTMLSpanElement;
const fpsCheckbox = document.getElementById('setting-fps') as HTMLInputElement;

// Audio Settings Elements (in pause menu)
const audioMasterVolume = document.getElementById('audio-master-volume') as HTMLInputElement;
const audioMasterEnabled = document.getElementById('audio-master-enabled') as HTMLInputElement;
const audioMasterValue = document.getElementById('audio-master-value') as HTMLSpanElement;
const audioMusicVolume = document.getElementById('audio-music-volume') as HTMLInputElement;
const audioMusicEnabled = document.getElementById('audio-music-enabled') as HTMLInputElement;
const audioMusicValue = document.getElementById('audio-music-value') as HTMLSpanElement;
const audioSfxVolume = document.getElementById('audio-sfx-volume') as HTMLInputElement;
const audioSfxEnabled = document.getElementById('audio-sfx-enabled') as HTMLInputElement;
const audioSfxValue = document.getElementById('audio-sfx-value') as HTMLSpanElement;
const audioVoiceVolume = document.getElementById('audio-voice-volume') as HTMLInputElement;
const audioVoiceEnabled = document.getElementById('audio-voice-enabled') as HTMLInputElement;
const audioVoiceValue = document.getElementById('audio-voice-value') as HTMLSpanElement;
const audioUiVolume = document.getElementById('audio-ui-volume') as HTMLInputElement;
const audioUiEnabled = document.getElementById('audio-ui-enabled') as HTMLInputElement;
const audioUiValue = document.getElementById('audio-ui-value') as HTMLSpanElement;

// Help Panel Element
const helpPanel = document.getElementById('help-panel') as HTMLDivElement;
let helpPanelVisible = false;
let helpAutoHideTimer: number | null = null;

// Buy Menu Elements
const buyMenu = document.getElementById('buy-menu') as HTMLDivElement;
const buyCredits = document.getElementById('buy-credits') as HTMLSpanElement;
const weaponItems = buyMenu.querySelectorAll('.weapon-item') as NodeListOf<HTMLDivElement>;
let buyMenuAutoCloseTimer: number | null = null;

// Round Over Panel Elements
const roundOverPanel = document.getElementById('round-over-panel') as HTMLDivElement;
const winnerText = document.getElementById('winner-text') as HTMLDivElement;
const matchInfo = document.getElementById('match-info') as HTMLDivElement;
const scoreAttackers = document.getElementById('score-attackers') as HTMLDivElement;
const scoreDefenders = document.getElementById('score-defenders') as HTMLDivElement;
const playAgainButton = document.getElementById('btn-play-again') as HTMLButtonElement;
const mainMenuButton = document.getElementById('btn-main-menu') as HTMLButtonElement;
const countdownDisplay = document.getElementById('countdown-display') as HTMLDivElement;
const countdownText = document.getElementById('countdown-text') as HTMLDivElement;
const pauseCountdownButton = document.getElementById('btn-pause-countdown') as HTMLButtonElement;

// Room Code Display Elements
const roomCodeDisplay = document.getElementById('room-code-display') as HTMLDivElement;
const roomCodeText = document.getElementById('room-code-text') as HTMLSpanElement;
const copyCodeButton = document.getElementById('btn-copy-code') as HTMLButtonElement;

// Lobby Screen Elements
const lobbyScreen = document.getElementById('lobby-screen') as HTMLDivElement;
const lobbyRoomCode = document.getElementById('lobby-room-code') as HTMLDivElement;
const hostSettings = document.getElementById('host-settings') as HTMLDivElement;
const lobbyPlayerCountSelect = document.getElementById('lobby-player-count') as HTMLSelectElement;
const lobbyRoundCountSelect = document.getElementById('lobby-round-count') as HTMLSelectElement;
const lobbyCurrentPlayers = document.getElementById('lobby-current-players') as HTMLSpanElement;
const lobbyMaxPlayers = document.getElementById('lobby-max-players') as HTMLSpanElement;
const lobbyPlayerList = document.getElementById('lobby-player-list') as HTMLDivElement;
const lobbyWaitingText = document.getElementById('lobby-waiting-text') as HTMLDivElement;
const lobbyCountdown = document.getElementById('lobby-countdown') as HTMLDivElement;
const leaveLobbyButton = document.getElementById('btn-leave-lobby') as HTMLButtonElement;
const copyLobbyCodeButton = document.getElementById('btn-copy-lobby-code') as HTMLButtonElement;
const startGameButton = document.getElementById('btn-start-game') as HTMLButtonElement;
const lobbyMakePublicCheckbox = document.getElementById('lobby-make-public') as HTMLInputElement;

// Lobby Browser Elements
const findMatchButton = document.getElementById('btn-find-match') as HTMLButtonElement;
const lobbyBrowserModal = document.getElementById('lobby-browser-modal') as HTMLDivElement;
const lobbyBrowserCloseButton = document.getElementById('btn-lobby-browser-close') as HTMLButtonElement;
const refreshLobbiesButton = document.getElementById('btn-refresh-lobbies') as HTMLButtonElement;
const lobbyList = document.getElementById('lobby-list') as HTMLDivElement;
const lobbyEmpty = document.getElementById('lobby-empty') as HTMLDivElement;

// Loading Screen Elements
const loadingScreen = document.getElementById('loading-screen') as HTMLDivElement;
const loadingBar = document.getElementById('loading-bar') as HTMLDivElement;
const loadingPercentage = document.getElementById('loading-percentage') as HTMLDivElement;
const loadingText = document.getElementById('loading-text') as HTMLDivElement;

// Music Player Elements
const musicPlayerElement = document.getElementById('music-player') as HTMLDivElement;
const musicHeader = document.getElementById('music-header') as HTMLDivElement;
const musicPausePlayBtn = document.getElementById('music-pause-play') as HTMLButtonElement;
const musicStopBtn = document.getElementById('music-stop') as HTMLButtonElement;
const musicTracks = Array.from(document.querySelectorAll('.music-track')) as HTMLDivElement[];

// Game instance
let game: Game | null = null;

// Hero ability names for UI display
const HERO_ABILITIES: Record<string, { ability1: string; ability2: string }> = {
  vanta: { ability1: 'Energy Shield', ability2: 'Shockwave Slam' },
  spectre: { ability1: 'Cloak', ability2: 'Reveal Pulse' },
  katana: { ability1: 'Blink', ability2: 'Invisibility' },
  byte: { ability1: 'EMP Pulse', ability2: 'Shock Trap' },
  glyph: { ability1: 'Poison Cloud', ability2: 'Turret Deploy' },
  spark: { ability1: 'Fire Bottle', ability2: 'Minigun Mode' },
  zero: { ability1: 'Mark Target', ability2: 'Dash Back' },
  mira: { ability1: 'Heal Field', ability2: 'Shield Burst' },
};

// Game settings
let gameSettings: GameSettings = {
  enemyBotCount: 3,
  friendlyBotCount: 0,
  botDifficulty: 'medium',
  gameMode: 'deathmatch',
  mapId: 'neon_arena',
  playerHero: 'vanta',
  abilityQSlot: 1,  // Which ability (1 or 2) is on Q key
  abilityFSlot: 2,  // Which ability (1 or 2) is on F key
  totalRounds: 5,   // Best of X rounds
  aiMode: 'rule_based',  // Bot AI mode (rule_based or slm)
};

// Map info for hints
// Populate map dropdown based on game mode
function populateMapDropdown(gameMode: string): void {
  const currentMapId = mapSelect.value;
  mapSelect.innerHTML = '';
  
  // Filter maps based on game mode
  const filteredMaps = MAP_REGISTRY.filter(map => {
    if (gameMode === 'spike') {
      return map.hasSpikeSites; // Only show maps with spike sites
    } else {
      return !map.hasSpikeSites; // Only show deathmatch maps
    }
  });
  
  // Add options
  for (const map of filteredMaps) {
    const option = document.createElement('option');
    option.value = map.id;
    option.textContent = `${map.name} - ${map.description}`;
    mapSelect.appendChild(option);
  }
  
  // Try to keep current selection if valid, otherwise select first
  const validIds = filteredMaps.map(m => m.id);
  if (validIds.includes(currentMapId)) {
    mapSelect.value = currentMapId;
  } else if (filteredMaps.length > 0) {
    mapSelect.value = filteredMaps[0]!.id;
  }
  
  updateMapHint();
}

// Update map hint based on selection
function updateMapHint(): void {
  const mapId = mapSelect.value;
  const mapInfo = MAP_REGISTRY.find(m => m.id === mapId);
  if (mapInfo) {
    const gameMode = gameModeSelect.value;
    if (gameMode === 'spike' && !mapInfo.hasSpikeSites) {
      mapHint.textContent = '⚠️ This map has no spike sites!';
      mapHint.style.color = '#ff6b6b';
    } else if (gameMode === 'deathmatch' && mapInfo.hasSpikeSites) {
      mapHint.textContent = 'ℹ️ Spike sites available but unused in Deathmatch';
      mapHint.style.color = '#4ecdc4';
    } else {
      mapHint.textContent = `✓ ${mapInfo.description}`;
      mapHint.style.color = '#2ecc71';
    }
  } else {
    mapHint.textContent = '';
  }
}

// Update ability name displays based on hero and slot selections
function updateAbilityNames(): void {
  const hero = heroSelect.value;
  const abilities = HERO_ABILITIES[hero] || { ability1: 'Ability 1', ability2: 'Ability 2' };
  
  const qSlot = parseInt(abilityQSelect.value);
  const fSlot = parseInt(abilityFSelect.value);
  
  abilityQName.textContent = qSlot === 1 ? abilities.ability1 : abilities.ability2;
  abilityFName.textContent = fSlot === 1 ? abilities.ability1 : abilities.ability2;
}

// -------------------------------------------------------------------------
// Stats Modal Functions
// -------------------------------------------------------------------------

// Country code to flag emoji
function countryFlag(code: string): string {
  if (!code || code === 'XX') return '🌐';
  return code.toUpperCase().replace(/./g, c => 
    String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))
  );
}

// Format large numbers
function formatStatNumber(num: number): string {
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}

// Load stats modal data
async function loadStatsModal(): Promise<void> {
  // Initialize PlayerStatsService if not already
  await PlayerStatsService.initialize();
  
  // Get DOM elements
  const totalPlayersEl = document.getElementById('stats-total-players');
  const totalGamesEl = document.getElementById('stats-total-games');
  const totalHoursEl = document.getElementById('stats-total-hours');
  const myStatsEl = document.getElementById('stats-my-stats');
  const countriesEl = document.getElementById('stats-countries');
  const leaderboardEl = document.getElementById('stats-leaderboard');
  
  // Load global stats
  try {
    const globalStats = await PlayerStatsService.getGlobalStats();
    if (globalStats) {
      if (totalPlayersEl) totalPlayersEl.textContent = formatStatNumber(globalStats.totalUniquePlayers || 0);
      if (totalGamesEl) totalGamesEl.textContent = formatStatNumber(globalStats.totalGamesPlayed || 0);
      if (totalHoursEl) totalHoursEl.textContent = formatStatNumber(Math.floor(globalStats.totalPlayTimeHours || 0));
      
      // Countries
      if (countriesEl && globalStats.playersByCountry) {
        const sorted = Object.entries(globalStats.playersByCountry)
          .sort((a, b) => (b[1] as number) - (a[1] as number))
          .slice(0, 8);
        
        if (sorted.length === 0) {
          countriesEl.innerHTML = '<span class="stats-loading">No country data yet</span>';
        } else {
          countriesEl.innerHTML = sorted
            .map(([code, count]) => `
              <span class="country-tag">
                ${countryFlag(code)} ${code}
                <span class="count">${count}</span>
              </span>
            `)
            .join('');
        }
      }
    }
  } catch (e) {
    console.warn('[Stats] Failed to load global stats:', e);
  }
  
  // Load my stats
  try {
    const myStats = await PlayerStatsService.getMyStats();
    if (myStatsEl) {
      if (myStats) {
        const winRate = myStats.gamesPlayed > 0 
          ? Math.round((myStats.gamesWon / myStats.gamesPlayed) * 100) 
          : 0;
        const kd = myStats.totalDeaths > 0 
          ? (myStats.totalKills / myStats.totalDeaths).toFixed(2) 
          : myStats.totalKills.toString();
        
        myStatsEl.innerHTML = `
          <div class="my-stat">
            <span class="my-stat-label">Games Played</span>
            <span class="my-stat-value">${myStats.gamesPlayed}</span>
          </div>
          <div class="my-stat">
            <span class="my-stat-label">Wins</span>
            <span class="my-stat-value">${myStats.gamesWon} (${winRate}%)</span>
          </div>
          <div class="my-stat">
            <span class="my-stat-label">Total Kills</span>
            <span class="my-stat-value">${myStats.totalKills}</span>
          </div>
          <div class="my-stat">
            <span class="my-stat-label">K/D Ratio</span>
            <span class="my-stat-value">${kd}</span>
          </div>
          <div class="my-stat">
            <span class="my-stat-label">Play Time</span>
            <span class="my-stat-value">${Math.floor(myStats.totalPlayTimeSeconds / 60)} min</span>
          </div>
          <div class="my-stat">
            <span class="my-stat-label">Country</span>
            <span class="my-stat-value">${countryFlag(myStats.country)} ${myStats.countryName || 'Unknown'}</span>
          </div>
        `;
      } else {
        myStatsEl.innerHTML = '<div class="stats-loading">Play a game to see your stats!</div>';
      }
    }
  } catch (e) {
    console.warn('[Stats] Failed to load my stats:', e);
    if (myStatsEl) myStatsEl.innerHTML = '<div class="stats-loading">Play a game to see your stats!</div>';
  }
  
  // Load leaderboard
  try {
    const topPlayers = await loadLeaderboardData();
    if (leaderboardEl) {
      if (topPlayers.length === 0) {
        leaderboardEl.innerHTML = '<li class="stats-loading">No players yet</li>';
      } else {
        leaderboardEl.innerHTML = topPlayers
          .map((player, index) => {
            const winRate = player.gamesPlayed > 0 
              ? Math.round((player.gamesWon / player.gamesPlayed) * 100) 
              : 0;
            return `
              <li>
                <span class="rank">#${index + 1}</span>
                <span class="name">${player.callsign || 'Unknown'}</span>
                <span class="wins">${player.gamesWon || 0} wins (${winRate}%)</span>
              </li>
            `;
          })
          .join('');
      }
    }
  } catch (e) {
    console.warn('[Stats] Failed to load leaderboard:', e);
    if (leaderboardEl) leaderboardEl.innerHTML = '<li class="stats-loading">Failed to load</li>';
  }
}

// Load leaderboard data from Firestore
async function loadLeaderboardData(): Promise<any[]> {
  try {
    const { getFirestore, collection, query, orderBy, limit, getDocs } = await import('firebase/firestore');
    const { initializeApp, getApps, getApp } = await import('firebase/app');
    
    const app = getApps().length === 0 ? initializeApp(FIREBASE_CONFIG) : getApp();
    const db = getFirestore(app);
    
    const q = query(
      collection(db, 'playerStats'),
      orderBy('gamesWon', 'desc'),
      limit(5)
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => doc.data());
  } catch (e) {
    console.warn('[Stats] Failed to query leaderboard:', e);
    return [];
  }
}

// -------------------------------------------------------------------------
// Lobby Browser Functions
// -------------------------------------------------------------------------

let lobbyBrowserUnsubscribe: (() => void) | null = null;

async function openLobbyBrowser(): Promise<void> {
  console.log('[LobbyBrowser] Opening lobby browser...');
  
  // Validate player name first
  const playerName = playerNameInput.value.trim();
  if (!playerName) {
    console.log('[LobbyBrowser] No player name entered');
    playerNameInput.focus();
    playerNameInput.style.borderColor = '#ff4444';
    setTimeout(() => { playerNameInput.style.borderColor = ''; }, 2000);
    return;
  }
  
  lobbyBrowserModal.classList.remove('hidden');
  connectionUI.classList.add('hidden');
  
  try {
    // Unsubscribe from any existing subscription first
    if (lobbyBrowserUnsubscribe) {
      lobbyBrowserUnsubscribe();
      lobbyBrowserUnsubscribe = null;
    }
    
    // Initialize service and fetch lobbies
    console.log('[LobbyBrowser] Initializing service...');
    await LobbyBrowserService.initialize();
    
    // Clean up stale lobbies first
    console.log('[LobbyBrowser] Cleaning stale lobbies...');
    await LobbyBrowserService.cleanupStaleLobbies();
    
    // Subscribe to real-time updates (this will immediately fire with current data)
    console.log('[LobbyBrowser] Subscribing to lobbies...');
    lobbyBrowserUnsubscribe = LobbyBrowserService.subscribeToLobbies((lobbies) => {
      console.log('[LobbyBrowser] Real-time update:', lobbies.length, 'lobbies');
      renderLobbies(lobbies);
    });
    
    console.log('[LobbyBrowser] Lobby browser ready');
  } catch (e) {
    console.error('[LobbyBrowser] Error opening lobby browser:', e);
  }
}

function closeLobbyBrowser(): void {
  lobbyBrowserModal.classList.add('hidden');
  connectionUI.classList.remove('hidden');
  
  // Unsubscribe from updates
  if (lobbyBrowserUnsubscribe) {
    lobbyBrowserUnsubscribe();
    lobbyBrowserUnsubscribe = null;
  }
}

async function refreshLobbies(): Promise<void> {
  // Add spinning animation to refresh button
  refreshLobbiesButton.classList.add('spinning');
  
  try {
    const lobbies = await LobbyBrowserService.getPublicLobbies();
    renderLobbies(lobbies);
  } catch (e) {
    console.error('[LobbyBrowser] Failed to refresh lobbies:', e);
  }
  
  // Remove spinning animation after short delay
  setTimeout(() => {
    refreshLobbiesButton.classList.remove('spinning');
  }, 500);
}

function renderLobbies(lobbies: PublicLobby[]): void {
  // Clear existing lobby items (but not the empty state)
  const existingItems = lobbyList.querySelectorAll('.lobby-item');
  existingItems.forEach(item => item.remove());
  
  if (lobbies.length === 0) {
    lobbyEmpty.style.display = 'block';
    return;
  }
  
  lobbyEmpty.style.display = 'none';
  
  for (const lobby of lobbies) {
    const item = document.createElement('div');
    item.className = 'lobby-item';
    
    const modeLabel = lobby.gameMode === 'spike' ? '💾 Spike' : '⚔️ Deathmatch';
    const mapInfo = MAP_REGISTRY.find(m => m.id === lobby.mapId);
    const mapName = mapInfo?.name || lobby.mapId;
    
    item.innerHTML = `
      <div class="lobby-info">
        <div class="lobby-host">${escapeHtml(lobby.hostName)}</div>
        <div class="lobby-details">
          <span>${modeLabel}</span>
          <span>📍 ${mapName}</span>
        </div>
      </div>
      <span class="lobby-players">${lobby.currentPlayers}/${lobby.maxPlayers}</span>
      <button class="join-btn" data-room-code="${lobby.roomCode}">Join</button>
    `;
    
    // Add click handler for join button
    const joinBtn = item.querySelector('.join-btn') as HTMLButtonElement;
    joinBtn.addEventListener('click', () => {
      joinPublicLobby(lobby.roomCode);
    });
    
    lobbyList.insertBefore(item, lobbyEmpty);
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

async function joinPublicLobby(roomCode: string): Promise<void> {
  // Close the lobby browser
  closeLobbyBrowser();
  
  // Set the room code and trigger the join flow
  roomCodeInput.value = roomCode;
  
  // Trigger the same flow as manual join
  await handleConnect();
}

// Load saved settings
function loadSettings(): void {
  const saved = localStorage.getItem('cybergrid_settings');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      gameSettings = { ...gameSettings, ...parsed };
      
      // Validate ability slot settings - must be 1 or 2, and different from each other
      if (gameSettings.abilityQSlot !== 1 && gameSettings.abilityQSlot !== 2) {
        gameSettings.abilityQSlot = 1;
      }
      if (gameSettings.abilityFSlot !== 1 && gameSettings.abilityFSlot !== 2) {
        gameSettings.abilityFSlot = 2;
      }
      // If both are the same, reset to defaults
      if (gameSettings.abilityQSlot === gameSettings.abilityFSlot) {
        gameSettings.abilityQSlot = 1;
        gameSettings.abilityFSlot = 2;
      }
      
      console.log('[Settings] Loaded:', gameSettings);
    } catch (e) {
      console.warn('Failed to load settings:', e);
    }
  }
  
  // Apply to UI
  botCountSlider.value = String(gameSettings.enemyBotCount);
  botCountValue.textContent = String(gameSettings.enemyBotCount);
  friendlyBotsSlider.value = String(gameSettings.friendlyBotCount);
  friendlyBotsValue.textContent = String(gameSettings.friendlyBotCount);
  botDifficultySelect.value = gameSettings.botDifficulty;
  aiModeSelect.value = gameSettings.aiMode || 'rule_based';
  gameModeSelect.value = gameSettings.gameMode;
  
  // Populate maps based on game mode, then set saved map
  populateMapDropdown(gameSettings.gameMode);
  if (gameSettings.mapId) {
    mapSelect.value = gameSettings.mapId;
  }
  
  heroSelect.value = gameSettings.playerHero;
  abilityQSelect.value = String(gameSettings.abilityQSlot);
  abilityFSelect.value = String(gameSettings.abilityFSlot);
  updateAbilityNames();
  updateMapHint();
}

function saveSettings(): void {
  gameSettings = {
    enemyBotCount: parseInt(botCountSlider.value),
    friendlyBotCount: parseInt(friendlyBotsSlider.value),
    botDifficulty: botDifficultySelect.value as 'easy' | 'medium' | 'hard',
    gameMode: gameModeSelect.value as 'deathmatch' | 'spike',
    mapId: mapSelect.value,
    playerHero: heroSelect.value,
    abilityQSlot: parseInt(abilityQSelect.value) as 1 | 2,
    abilityFSlot: parseInt(abilityFSelect.value) as 1 | 2,
    totalRounds: gameSettings.totalRounds,  // Preserve from lobby setting
    aiMode: aiModeSelect.value as 'rule_based' | 'slm',
  };
  localStorage.setItem('cybergrid_settings', JSON.stringify(gameSettings));
  settingsPanel.classList.add('hidden');
}

// Gameplay settings (saved separately)
interface GameplaySettings {
  volume: number;
  sensitivity: number;
  showFps: boolean;
}

// Audio settings interface
interface AudioSettingsStorage {
  masterVolume: number;
  masterEnabled: boolean;
  musicVolume: number;
  musicEnabled: boolean;
  sfxVolume: number;
  sfxEnabled: boolean;
  voiceVolume: number;
  voiceEnabled: boolean;
  uiVolume: number;
  uiEnabled: boolean;
}

let gameplaySettings: GameplaySettings = {
  volume: 100,
  sensitivity: 1.0,
  showFps: true,
};

let audioSettings: AudioSettingsStorage = {
  masterVolume: 80,
  masterEnabled: true,
  musicVolume: 50,
  musicEnabled: true,
  sfxVolume: 80,
  sfxEnabled: true,
  voiceVolume: 100,
  voiceEnabled: true,
  uiVolume: 70,
  uiEnabled: true,
};

function loadAudioSettings(): void {
  const saved = localStorage.getItem('cybergrid_audio');
  if (saved) {
    try {
      audioSettings = { ...audioSettings, ...JSON.parse(saved) };
    } catch (e) {
      console.warn('Failed to load audio settings:', e);
    }
  }
  // Apply to UI (both panels)
  updateAudioSettingsUI();
  // Apply to uiAudio for menu sounds
  const masterVol = audioSettings.masterVolume / 100;
  const uiVol = audioSettings.uiVolume / 100;
  uiAudio.setVolume(masterVol * uiVol);
  uiAudio.setEnabled(audioSettings.masterEnabled && audioSettings.uiEnabled);
  // Apply to music player
  const musicVol = audioSettings.musicVolume / 100;
  musicPlayer.setVolume(masterVol * musicVol);
}

function saveAudioSettings(): void {
  localStorage.setItem('cybergrid_audio', JSON.stringify(audioSettings));
}

function updateAudioSettingsUI(): void {
  // Update pause menu audio controls
  audioMasterVolume.value = String(audioSettings.masterVolume);
  audioMasterEnabled.checked = audioSettings.masterEnabled;
  audioMasterValue.textContent = `${audioSettings.masterVolume}%`;
  audioMasterVolume.disabled = !audioSettings.masterEnabled;
  
  audioMusicVolume.value = String(audioSettings.musicVolume);
  audioMusicEnabled.checked = audioSettings.musicEnabled;
  audioMusicValue.textContent = `${audioSettings.musicVolume}%`;
  audioMusicVolume.disabled = !audioSettings.musicEnabled;
  
  audioSfxVolume.value = String(audioSettings.sfxVolume);
  audioSfxEnabled.checked = audioSettings.sfxEnabled;
  audioSfxValue.textContent = `${audioSettings.sfxVolume}%`;
  audioSfxVolume.disabled = !audioSettings.sfxEnabled;
  
  audioVoiceVolume.value = String(audioSettings.voiceVolume);
  audioVoiceEnabled.checked = audioSettings.voiceEnabled;
  audioVoiceValue.textContent = `${audioSettings.voiceVolume}%`;
  audioVoiceVolume.disabled = !audioSettings.voiceEnabled;
  
  audioUiVolume.value = String(audioSettings.uiVolume);
  audioUiEnabled.checked = audioSettings.uiEnabled;
  audioUiValue.textContent = `${audioSettings.uiVolume}%`;
  audioUiVolume.disabled = !audioSettings.uiEnabled;
  
  // Update settings panel audio controls
  settingsAudioMasterVolume.value = String(audioSettings.masterVolume);
  settingsAudioMasterEnabled.checked = audioSettings.masterEnabled;
  settingsAudioMasterValue.textContent = `${audioSettings.masterVolume}%`;
  settingsAudioMasterVolume.disabled = !audioSettings.masterEnabled;
  
  settingsAudioMusicVolume.value = String(audioSettings.musicVolume);
  settingsAudioMusicEnabled.checked = audioSettings.musicEnabled;
  settingsAudioMusicValue.textContent = `${audioSettings.musicVolume}%`;
  settingsAudioMusicVolume.disabled = !audioSettings.musicEnabled;
  
  settingsAudioSfxVolume.value = String(audioSettings.sfxVolume);
  settingsAudioSfxEnabled.checked = audioSettings.sfxEnabled;
  settingsAudioSfxValue.textContent = `${audioSettings.sfxVolume}%`;
  settingsAudioSfxVolume.disabled = !audioSettings.sfxEnabled;
  
  settingsAudioVoiceVolume.value = String(audioSettings.voiceVolume);
  settingsAudioVoiceEnabled.checked = audioSettings.voiceEnabled;
  settingsAudioVoiceValue.textContent = `${audioSettings.voiceVolume}%`;
  settingsAudioVoiceVolume.disabled = !audioSettings.voiceEnabled;
  
  settingsAudioUiVolume.value = String(audioSettings.uiVolume);
  settingsAudioUiEnabled.checked = audioSettings.uiEnabled;
  settingsAudioUiValue.textContent = `${audioSettings.uiVolume}%`;
  settingsAudioUiVolume.disabled = !audioSettings.uiEnabled;
}

function loadGameplaySettings(): void {
  const saved = localStorage.getItem('cybergrid_gameplay');
  if (saved) {
    try {
      gameplaySettings = { ...gameplaySettings, ...JSON.parse(saved) };
    } catch (e) {
      console.warn('Failed to load gameplay settings:', e);
    }
  }
  updatePauseMenuUI();
}

function saveGameplaySettings(): void {
  gameplaySettings = {
    volume: 100, // Deprecated, now using audio categories
    sensitivity: parseFloat(sensitivitySlider.value) / 10,
    showFps: fpsCheckbox.checked,
  };
  localStorage.setItem('cybergrid_gameplay', JSON.stringify(gameplaySettings));
}

function updatePauseMenuUI(): void {
  sensitivitySlider.value = String(Math.round(gameplaySettings.sensitivity * 10));
  sensitivityValue.textContent = gameplaySettings.sensitivity.toFixed(1);
  fpsCheckbox.checked = gameplaySettings.showFps;
  
  // Update audio settings UI from game if available
  if (game) {
    const audioSettings = game.getAudioSettings();
    if (audioSettings) {
      audioMasterVolume.value = String(audioSettings.masterVolume);
      audioMasterEnabled.checked = audioSettings.masterEnabled;
      audioMasterValue.textContent = `${audioSettings.masterVolume}%`;
      audioMasterVolume.disabled = !audioSettings.masterEnabled;
      
      audioMusicVolume.value = String(audioSettings.musicVolume);
      audioMusicEnabled.checked = audioSettings.musicEnabled;
      audioMusicValue.textContent = `${audioSettings.musicVolume}%`;
      audioMusicVolume.disabled = !audioSettings.musicEnabled;
      
      audioSfxVolume.value = String(audioSettings.sfxVolume);
      audioSfxEnabled.checked = audioSettings.sfxEnabled;
      audioSfxValue.textContent = `${audioSettings.sfxVolume}%`;
      audioSfxVolume.disabled = !audioSettings.sfxEnabled;
      
      audioVoiceVolume.value = String(audioSettings.voiceVolume);
      audioVoiceEnabled.checked = audioSettings.voiceEnabled;
      audioVoiceValue.textContent = `${audioSettings.voiceVolume}%`;
      audioVoiceVolume.disabled = !audioSettings.voiceEnabled;
      
      audioUiVolume.value = String(audioSettings.uiVolume);
      audioUiEnabled.checked = audioSettings.uiEnabled;
      audioUiValue.textContent = `${audioSettings.uiVolume}%`;
      audioUiVolume.disabled = !audioSettings.uiEnabled;
    }
  }
}

// -------------------------------------------------------------------------
// Buy Menu Functions
// -------------------------------------------------------------------------

const WEAPON_PRICES: Record<string, number> = {
  'burst_pistol': 0,
  'neo_smg9': 1100,
  'pulse_rifle': 1500,
  'scatter_shot': 1400,
  'rail_sniper': 2100,
  'shard_launcher': 600,
  'nano_repeater': 900,
  'arc_welder': 1300,
  'phase_repeater': 1800,
  'plasma_cannon': 2800,
};

// Help Panel Functions
function showHelpPanel(autoHide: boolean = false): void {
  helpPanelVisible = true;
  helpPanel.classList.add('visible');
  
  // Clear any existing auto-hide timer
  if (helpAutoHideTimer !== null) {
    clearTimeout(helpAutoHideTimer);
    helpAutoHideTimer = null;
  }
  
  // Auto-hide after 5 seconds if requested
  if (autoHide) {
    helpAutoHideTimer = window.setTimeout(() => {
      hideHelpPanel();
    }, 5000);
  }
}

function hideHelpPanel(): void {
  if (!helpPanelVisible) return; // Already hidden
  helpPanelVisible = false;
  helpPanel.classList.remove('visible');
  if (helpAutoHideTimer !== null) {
    clearTimeout(helpAutoHideTimer);
    helpAutoHideTimer = null;
  }
}

function openBuyMenu(): void {
  if (!game) return;
  const player = game.getWorld().getLocalPlayer();
  if (!player || !player.alive) return;
  
  updateBuyMenuUI();
  buyMenu.classList.remove('hidden');
  
  // Start auto-close timer (3 seconds)
  startBuyMenuAutoCloseTimer();
}

function closeBuyMenu(): void {
  buyMenu.classList.add('hidden');
  
  // Clear auto-close timer
  if (buyMenuAutoCloseTimer !== null) {
    clearTimeout(buyMenuAutoCloseTimer);
    buyMenuAutoCloseTimer = null;
  }
}

function startBuyMenuAutoCloseTimer(): void {
  // Clear any existing timer
  if (buyMenuAutoCloseTimer !== null) {
    clearTimeout(buyMenuAutoCloseTimer);
  }
  
  // Set new timer for 3 seconds
  buyMenuAutoCloseTimer = window.setTimeout(() => {
    closeBuyMenu();
  }, 3000);
}

function updateBuyMenuUI(): void {
  if (!game) return;
  const player = game.getWorld().getLocalPlayer();
  if (!player) return;
  
  // Update credits display
  buyCredits.textContent = String(player.credits);
  
  // Update weapon item states
  weaponItems.forEach(item => {
    const weaponId = item.dataset.weapon || '';
    const slotStr = item.dataset.slot || '1';
    const slot = parseInt(slotStr) - 1;
    const price = WEAPON_PRICES[weaponId] || 0;
    
    // Check if player owns this weapon
    const owned = player.weaponInventory[slot] !== null;
    const canAfford = player.credits >= price;
    
    // Reset classes
    item.classList.remove('owned', 'cant-afford');
    
    if (owned) {
      item.classList.add('owned');
      const priceEl = item.querySelector('.weapon-price');
      if (priceEl) priceEl.textContent = 'OWNED';
    } else if (!canAfford && price > 0) {
      item.classList.add('cant-afford');
    }
  });
}

function tryBuyWeapon(weaponId: string): void {
  if (!game) return;
  const player = game.getWorld().getLocalPlayer();
  if (!player || !player.alive) return;
  
  // Map string to WeaponId enum
  const weaponIdMap: Record<string, import('@/shared/types').WeaponId> = {
    'burst_pistol': 'burst_pistol' as import('@/shared/types').WeaponId,
    'neo_smg9': 'neo_smg9' as import('@/shared/types').WeaponId,
    'pulse_rifle': 'pulse_rifle' as import('@/shared/types').WeaponId,
    'scatter_shot': 'scatter_shot' as import('@/shared/types').WeaponId,
    'rail_sniper': 'rail_sniper' as import('@/shared/types').WeaponId,
    'shard_launcher': 'shard_launcher' as import('@/shared/types').WeaponId,
    'nano_repeater': 'nano_repeater' as import('@/shared/types').WeaponId,
    'arc_welder': 'arc_welder' as import('@/shared/types').WeaponId,
    'phase_repeater': 'phase_repeater' as import('@/shared/types').WeaponId,
    'plasma_cannon': 'plasma_cannon' as import('@/shared/types').WeaponId,
  };
  
  const enumWeaponId = weaponIdMap[weaponId];
  if (!enumWeaponId) return;
  
  // Check if already owned
  const slot = player.getWeaponSlotForId(enumWeaponId) - 1;
  if (player.weaponInventory[slot] !== null) {
    // Already owned - just switch to it
    player.switchToWeaponSlot(slot + 1);
    closeBuyMenu();
    return;
  }
  
  // Try to buy
  const success = player.buyWeapon(enumWeaponId);
  if (success) {
    updateBuyMenuUI();
    // Play purchase sound or effect here
  }
  
  // Reset auto-close timer on any weapon interaction (not just successful purchase)
  startBuyMenuAutoCloseTimer();
}

function pauseGame(): void {
  if (!game) return;
  game.pause();
  pauseMenu.classList.remove('hidden');
}

function resumeGame(): void {
  if (!game) return;
  pauseMenu.classList.add('hidden');
  saveGameplaySettings();
  game.resume();
}

function quitToMenu(): void {
  if (!game) return;
  
  // End stats session before stopping game
  game.endStatsSession();
  
  // Cleanup voice chat
  cleanupVoiceChat();
  
  game.stop();
  game = null;
  gameStarted = false; // Reset so Start Game button works again
  stopRoundEndPolling();
  stopLobbyCountdown();
  
  // Hide all game UI
  canvas.classList.add('hidden');
  pauseMenu.classList.add('hidden');
  roundOverPanel.classList.add('hidden');
  roomCodeDisplay.classList.add('hidden');
  lobbyScreen.classList.add('hidden');
  helpPanel.classList.remove('visible');
  buyMenu.classList.add('hidden');
  
  // Show main menu UI
  connectionUI.classList.remove('hidden');
  settingsButton.classList.remove('hidden');
  
  // Reset join UI
  hostButton.classList.remove('hidden');
  joinButton.classList.remove('hidden');
  roomCodeInput.classList.add('hidden');
  joinButtons.classList.add('hidden');
  roomCodeInput.value = '';
  
  // Reset lobby state
  lobbyState.isHost = false;
  lobbyState.players = [];
  lobbyState.roomCode = '';
  lobbyState.isPublic = false;
  gameStarted = false;
  
  // Disconnect network if still connected
  if (lobbyNetwork) {
    lobbyNetwork.disconnect();
    lobbyNetwork = null;
  }
}

// -------------------------------------------------------------------------
// Lobby System
// -------------------------------------------------------------------------

interface LobbyPlayer {
  id: string;
  name: string;
  isHost: boolean;
}

interface LobbyState {
  isHost: boolean;
  roomCode: string;
  players: LobbyPlayer[];
  maxPlayers: number;
  countdownActive: boolean;
  countdownValue: number;
  isPublic: boolean;
}

const lobbyState: LobbyState = {
  isHost: false,
  roomCode: '',
  players: [],
  maxPlayers: 2,
  countdownActive: false,
  countdownValue: 5,
  isPublic: false,
};

let lobbyCountdownInterval: number | null = null;

function generateRoomCode(): string {
  const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const digits = '0123456789';
  
  let code = '';
  for (let i = 0; i < 4; i++) {
    code += letters.charAt(Math.floor(Math.random() * letters.length));
  }
  for (let i = 0; i < 2; i++) {
    code += digits.charAt(Math.floor(Math.random() * digits.length));
  }
  
  return code;
}

function showLobby(isHost: boolean, roomCode: string, playerName: string): void {
  // Reset game started flag so Start Game button works
  gameStarted = false;
  
  lobbyState.isHost = isHost;
  lobbyState.roomCode = roomCode;
  lobbyState.maxPlayers = parseInt(lobbyPlayerCountSelect.value);
  lobbyState.countdownActive = false;
  lobbyState.isPublic = isHost ? lobbyMakePublicCheckbox.checked : false;
  
  // Add self as first player
  lobbyState.players = [{
    id: 'local',
    name: playerName,
    isHost: isHost,
  }];
  
  // Update UI
  lobbyRoomCode.textContent = roomCode;
  lobbyMaxPlayers.textContent = String(lobbyState.maxPlayers);
  
  // Show/hide host controls - ONLY host sees these
  hostSettings.style.display = isHost ? 'block' : 'none';
  startGameButton.style.display = isHost ? 'block' : 'none';
  
  // Update waiting text based on role
  if (isHost) {
    lobbyWaitingText.innerHTML = 'Waiting for players<span class="dots">...</span>';
  } else {
    lobbyWaitingText.innerHTML = 'Connecting to host<span class="dots">...</span>';
  }
  
  // Hide connection UI, show lobby
  connectionUI.classList.add('hidden');
  settingsPanel.classList.add('hidden');
  settingsButton.classList.add('hidden');
  lobbyScreen.classList.remove('hidden');
  
  // If host, sync the Make Public checkbox state with lobbyState
  // Don't auto-create here - let the checkbox change handler do it
  // This prevents duplicate lobby creation
  if (isHost) {
    lobbyMakePublicCheckbox.checked = lobbyState.isPublic;
    
    // Disable "Make Public" for solo play (1 player)
    if (lobbyState.maxPlayers === 1) {
      lobbyMakePublicCheckbox.disabled = true;
      lobbyMakePublicCheckbox.checked = false;
      lobbyState.isPublic = false;
    } else {
      lobbyMakePublicCheckbox.disabled = false;
    }
    
    console.log('[Lobby] isHost:', isHost, 'isPublic:', lobbyState.isPublic);
  }
  
  updateLobbyUI();
}

function updateLobbyUI(): void {
  // Update player count
  lobbyCurrentPlayers.textContent = String(lobbyState.players.length);
  lobbyMaxPlayers.textContent = String(lobbyState.maxPlayers);
  
  // Update player list
  lobbyPlayerList.innerHTML = '';
  for (const player of lobbyState.players) {
    const item = document.createElement('div');
    item.className = `player-item${player.isHost ? ' host' : ''}`;
    item.innerHTML = `
      <span class="name">${player.name}</span>
      ${player.isHost ? '<span class="tag">Host</span>' : ''}
    `;
    lobbyPlayerList.appendChild(item);
  }
  
  // Update start button state (host only)
  if (lobbyState.isHost) {
    const canStart = lobbyState.players.length >= lobbyState.maxPlayers || lobbyState.maxPlayers === 1;
    startGameButton.disabled = !canStart;
    
    if (canStart && !lobbyState.countdownActive) {
      lobbyWaitingText.textContent = 'All players ready!';
    } else if (!lobbyState.countdownActive) {
      lobbyWaitingText.innerHTML = 'Waiting for players<span class="dots">...</span>';
    }
    
    // Update public lobby player count
    if (lobbyState.isPublic) {
      LobbyBrowserService.updateLobbyPlayerCount(lobbyState.roomCode, lobbyState.players.length);
    }
  } else {
    // Joiner - show different message based on connection state
    const hasHost = lobbyState.players.some(p => p.isHost && p.id !== 'local');
    if (hasHost) {
      lobbyWaitingText.innerHTML = 'Waiting for host to start<span class="dots">...</span>';
    } else {
      lobbyWaitingText.innerHTML = 'Connecting to host<span class="dots">...</span>';
    }
  }
  
  // Auto-start countdown when full
  if (lobbyState.players.length >= lobbyState.maxPlayers && !lobbyState.countdownActive && lobbyState.maxPlayers > 1) {
    startLobbyCountdown();
  }
}

function startLobbyCountdown(): void {
  if (lobbyState.countdownActive) return;
  
  lobbyState.countdownActive = true;
  lobbyState.countdownValue = 5;
  
  lobbyWaitingText.classList.add('hidden');
  lobbyCountdown.classList.remove('hidden');
  lobbyCountdown.textContent = String(lobbyState.countdownValue);
  
  lobbyCountdownInterval = window.setInterval(() => {
    lobbyState.countdownValue--;
    lobbyCountdown.textContent = String(lobbyState.countdownValue);
    
    if (lobbyState.countdownValue <= 0) {
      stopLobbyCountdown();
      startGameFromLobby();
    }
  }, 1000);
}

function stopLobbyCountdown(): void {
  if (lobbyCountdownInterval !== null) {
    clearInterval(lobbyCountdownInterval);
    lobbyCountdownInterval = null;
  }
  lobbyState.countdownActive = false;
  lobbyCountdown.classList.add('hidden');
  lobbyWaitingText.classList.remove('hidden');
}

let gameStarted = false; // Prevent double start

function leaveLobby(): void {
  stopLobbyCountdown();
  lobbyScreen.classList.add('hidden');
  connectionUI.classList.remove('hidden');
  settingsButton.classList.remove('hidden');
  
  // Remove from public lobby listings if host
  if (lobbyState.isHost && lobbyState.isPublic) {
    LobbyBrowserService.removeLobby(lobbyState.roomCode);
  }
  
  // Reset join UI
  hostButton.classList.remove('hidden');
  joinButton.classList.remove('hidden');
  roomCodeInput.classList.add('hidden');
  joinButtons.classList.add('hidden');
  roomCodeInput.value = '';
  
  // Reset lobby state
  lobbyState.isHost = false;
  lobbyState.players = [];
  lobbyState.roomCode = '';
  lobbyState.isPublic = false;
  gameStarted = false; // Reset game started flag
  
  // Disconnect lobby network if exists
  if (lobbyNetwork) {
    lobbyNetwork.disconnect();
    lobbyNetwork = null;
  }
}

/**
 * Preload all game assets with loading screen
 */
async function preloadAssets(): Promise<void> {
  // Show loading screen
  loadingScreen.classList.remove('hidden');
  
  const updateProgress = (percent: number, text: string) => {
    loadingBar.style.width = `${percent}%`;
    loadingPercentage.textContent = `${Math.floor(percent)}%`;
    loadingText.textContent = text;
  };
  
  try {
    updateProgress(0, 'Initializing Audio System...');
    
    // Initialize game audio
    const gameAudio = getGameAudio();
    await gameAudio.init();
    updateProgress(10, 'Loading Sound Effects...');
    
    // Load all game sounds
    await gameAudio.loadAllSounds();
    updateProgress(90, 'Finalizing...');
    
    // Small delay for smooth UX
    await new Promise(resolve => setTimeout(resolve, 300));
    updateProgress(100, 'Ready!');
    
    // Wait a bit before hiding
    await new Promise(resolve => setTimeout(resolve, 500));
    
  } catch (error) {
    console.error('[Preload] Error loading assets:', error);
    updateProgress(100, 'Ready! (Some assets may load on-demand)');
    await new Promise(resolve => setTimeout(resolve, 1000));
  }
  
  // Hide loading screen
  loadingScreen.classList.add('hidden');
}

async function startGameFromLobby(): Promise<void> {
  // Prevent double execution
  if (gameStarted) {
    console.log(`[Main] startGameFromLobby already called, ignoring`);
    return;
  }
  gameStarted = true;
  
  const playerName = playerNameInput.value.trim() || 'Player';
  
  // Hide lobby
  lobbyScreen.classList.add('hidden');
  
  // Preload all assets with loading screen
  await preloadAssets();
  
  // Remove from public lobby listings (game is starting)
  if (lobbyState.isHost && lobbyState.isPublic) {
    LobbyBrowserService.removeLobby(lobbyState.roomCode);
  }
  
  // Update settings with lobby values
  gameSettings.totalRounds = parseInt(lobbyRoundCountSelect.value);
  
  // Get the network to pass to the game (don't disconnect it!)
  const networkToPass = lobbyNetwork;
  lobbyNetwork = null;  // Transfer ownership to game
  
  // Remove lobby event listeners - Game will set up its own
  if (networkToPass) {
    networkToPass.removeAllListeners();
    console.log(`[Main] Removed lobby listeners from network`);
  }
  
  // If host, broadcast game start to all peers BEFORE transferring network
  if (lobbyState.isHost && networkToPass) {
    console.log(`[Main] Broadcasting GameStart to all peers`);
    networkToPass.broadcast({
      type: PacketType.GameStart,
      timestamp: Date.now(),
      senderId: networkToPass.getLocalPeerId(),
      data: {
        roomCode: lobbyState.roomCode,
        totalRounds: gameSettings.totalRounds,
        mapId: gameSettings.mapId,
        gameMode: gameSettings.gameMode,
      },
    });
  }
  
  // Create game with settings and existing network connection
  // Pass lobby players so game knows their names
  console.log(`[Main] lobbyState.players RAW:`, JSON.stringify(lobbyState.players));
  const lobbyPlayersForGame = lobbyState.players.map(p => ({
    id: p.id,
    name: p.name,
    isHost: p.isHost,
  }));
  
  console.log(`[Main] Passing ${lobbyPlayersForGame.length} lobby players to game:`, JSON.stringify(lobbyPlayersForGame));
  console.log(`[Main] networkToPass exists: ${!!networkToPass}, peers: ${networkToPass?.getPeerIds().length || 0}`);
  
  if (networkToPass) {
    console.log(`[Main] Creating Game with EXISTING network (${networkToPass.getPeerIds().length} peers)`);
    game = new Game({
      canvas,
      playerName,
      settings: gameSettings,
      network: networkToPass,  // Pass existing lobby network
      lobbyPlayers: lobbyPlayersForGame,  // Pass lobby players with names
      appVersion: __APP_VERSION__,
    });
  } else {
    console.log(`[Main] Creating Game WITHOUT network (will create new)`);
    game = new Game({
      canvas,
      playerName,
      settings: gameSettings,
      lobbyPlayers: lobbyPlayersForGame,
      appVersion: __APP_VERSION__,
    });
  }

  await game.init();
  
  // Apply gameplay settings to game
  game.setShowFPS(gameplaySettings.showFps);
  
  // Apply audio settings to game
  game.updateAudioSettings(audioSettings);

  // Host game with the lobby's room code (game will use passed network)
  if (lobbyState.isHost) {
    const roomCode = await game.hostGame(lobbyState.roomCode);
    console.log(`[Main] Starting game as HOST: ${roomCode} (Best of ${gameSettings.totalRounds})`);
  } else {
    // Joining player - use joinGame instead
    await game.joinGame(lobbyState.roomCode);
    console.log(`[Main] Joined game: ${lobbyState.roomCode}`);
  }
  
  // Hide room code display during gameplay (only needed in lobby)
  roomCodeDisplay.classList.add('hidden');
  
  // Show game canvas
  canvas.classList.remove('hidden');

  // Start game loop
  game.start();
  
  // Start player stats tracking session
  game.startStatsSession();
  
  // Track match start in analytics
  game.trackMatchStart();
  
  // Show help panel briefly for new players
  showHelpPanel(true);
  
  // Start polling for round end
  startRoundEndPolling();
  
  // Initialize and show voice chat
  initVoiceChat();
}

// Round Over functions
let roundEndPollingId: number | null = null;
let roundOverShown = false;
let idleTimeoutId: number | null = null;
let lastInteractionTime: number = Date.now();

function showRoundOver(
  winningTeam: 'attackers' | 'defenders', 
  attackerScore: number, 
  defenderScore: number,
  isMatchOver: boolean,
  roundsToWin: number,
  isForfeit: boolean = false,
  forfeitPlayerName: string = ''
): void {
  // Get the round over panel header
  const header = roundOverPanel.querySelector('h2');
  
  // Update match info
  matchInfo.textContent = `First to ${roundsToWin} wins`;
  
  if (isMatchOver) {
    // Match is over - show match winner and Play Again button
    if (isForfeit) {
      if (header) header.textContent = '🚫 Player Disconnected';
      winnerText.textContent = `${forfeitPlayerName} has left the match`;
      winnerText.className = 'winner-text';
      matchInfo.textContent = 'Opponent forfeited - You Win!';
    } else if (winningTeam === 'attackers') {
      if (header) header.textContent = '🏆 Match Over!';
      winnerText.textContent = '⚡ ATTACKERS WIN THE MATCH!';
      winnerText.className = 'winner-text attackers';
    } else {
      if (header) header.textContent = '🏆 Match Over!';
      winnerText.textContent = '🛡️ DEFENDERS WIN THE MATCH!';
      winnerText.className = 'winner-text defenders';
    }
    
    if (!isForfeit) {
      matchInfo.textContent = 'Match Complete!';
    }
    
    // Show Play Again and Main Menu buttons, hide countdown
    playAgainButton.classList.remove('hidden');
    playAgainButton.textContent = 'Play Again';
    mainMenuButton.classList.remove('hidden');
    countdownDisplay.classList.add('hidden');
    
    // Record match result to player stats
    if (game) {
      game.recordMatchResult();
    }
    
    // Start idle timeout (60 seconds of no interaction)
    startIdleTimeout();
  } else {
    // Round is over - show round winner and countdown
    if (header) header.textContent = '🏆 Round Over';
    if (winningTeam === 'attackers') {
      winnerText.textContent = '⚡ Attackers Win Round!';
      winnerText.className = 'winner-text attackers';
    } else {
      winnerText.textContent = '🛡️ Defenders Win Round!';
      winnerText.className = 'winner-text defenders';
    }
    
    // Show countdown, hide Play Again and Main Menu buttons
    countdownDisplay.classList.remove('hidden');
    playAgainButton.classList.add('hidden');
    mainMenuButton.classList.add('hidden');
  }
  
  // Update scores
  scoreAttackers.textContent = String(attackerScore);
  scoreDefenders.textContent = String(defenderScore);
  
  // Show panel
  roundOverPanel.classList.remove('hidden');
  roundOverShown = true;
}

function updateCountdownDisplay(): void {
  if (!game) return;
  
  const countdownInfo = game.getCountdownInfo();
  if (!countdownInfo) return;
  
  const seconds = Math.ceil(countdownInfo.countdownTime / 1000);
  
  if (countdownInfo.isPaused) {
    countdownText.textContent = `PAUSED by ${countdownInfo.pausedBy}`;
    countdownText.classList.add('paused');
    pauseCountdownButton.textContent = '▶️ Resume';
    pauseCountdownButton.classList.add('paused');
  } else {
    countdownText.textContent = `Next round in ${seconds}s`;
    countdownText.classList.remove('paused');
    pauseCountdownButton.textContent = '⏸️ Pause';
    pauseCountdownButton.classList.remove('paused');
  }
}

function startRoundEndPolling(): void {
  roundOverShown = false;
  
  // Poll every 100ms
  roundEndPollingId = window.setInterval(() => {
    if (!game) {
      stopRoundEndPolling();
      return;
    }
    
    const info = game.getRoundEndInfo();
    if (info && !roundOverShown) {
      showRoundOver(
        info.winningTeam, 
        info.attackerScore, 
        info.defenderScore,
        info.isMatchOver,
        info.roundsToWin,
        info.isForfeit,
        info.forfeitPlayerName
      );
    }
    
    // Update countdown display while round over panel is shown
    if (roundOverShown && info && !info.isMatchOver) {
      updateCountdownDisplay();
    }
    
    // Hide round over panel when round becomes active again
    if (roundOverShown && game.isRoundActive()) {
      roundOverPanel.classList.add('hidden');
      roundOverShown = false;
    }
  }, 100);
}

function stopRoundEndPolling(): void {
  if (roundEndPollingId !== null) {
    clearInterval(roundEndPollingId);
    roundEndPollingId = null;
  }
  
  // Clear idle timeout if active
  clearIdleTimeout();
}

function startIdleTimeout(): void {
  // Clear any existing timeout
  clearIdleTimeout();
  
  // Reset last interaction time
  lastInteractionTime = Date.now();
  
  // Start checking for idle (every second)
  idleTimeoutId = window.setInterval(() => {
    const idleTime = Date.now() - lastInteractionTime;
    
    // If idle for 60 seconds, return to menu
    if (idleTime >= 60000) {
      console.log('[Main] 60 seconds idle - returning to menu');
      clearIdleTimeout();
      quitToMenu();
    }
  }, 1000);
}

function clearIdleTimeout(): void {
  if (idleTimeoutId !== null) {
    clearInterval(idleTimeoutId);
    idleTimeoutId = null;
  }
}

function resetIdleTimer(): void {
  lastInteractionTime = Date.now();
}

function playAgain(): void {
  if (!game) return;
  
  // User interaction - reset idle timer
  resetIdleTimer();
  clearIdleTimeout();
  
  // Check if this is match over
  const info = game.getRoundEndInfo();
  
  if (info?.isMatchOver) {
    // Match is over - full restart
    roundOverPanel.classList.add('hidden');
    roundOverShown = false;
    game.restartGame();
  }
  // Note: For round over, the countdown handles starting the next round automatically
}

function toggleCountdownPause(): void {
  if (!game) return;
  game.toggleCountdownPause();
}

// ============================================================================
// Voice Chat Functions
// ============================================================================

function initVoiceChat(): void {
  const voiceChat = VoiceChatService.getInstance();
  voiceChat.initialize();
  
  // Show microphone button during gameplay
  if (micToggleButton) {
    micToggleButton.classList.add('show');
    updateMicButtonUI(false);
  }
  
  // Set up voice chat based on game configuration
  if (game) {
    const localPlayer = game.getWorld().getLocalPlayer();
    const network = game.getNetwork();
    
    if (localPlayer && network) {
      // Get all players and filter by team (or all if 1v1)
      const allPlayers = game.getWorld().getPlayers();
      const is1v1 = allPlayers.length === 2;
      
      let allowedPeers: string[] = [];
      
      if (is1v1) {
        // In 1v1, allow communication with the other player
        allowedPeers = allPlayers
          .filter((p) => p.id !== localPlayer.id)
          .map((p) => p.peerId);
      } else {
        // In team games, only allow communication with teammates
        allowedPeers = allPlayers
          .filter((p) => p.team === localPlayer.team && p.id !== localPlayer.id)
          .map((p) => p.peerId);
      }
      
      voiceChat.setAllowedPeers(allowedPeers);
      
      // Register existing peer connections with voice chat
      for (const peerId of allowedPeers) {
        const peerConnection = network.getPeerConnection(peerId);
        if (peerConnection) {
          console.log(`[VoiceChat] Registering peer connection: ${peerId}`);
          voiceChat.addPeerConnection(peerId, peerConnection);
        }
      }
    }
  }
}

function cleanupVoiceChat(): void {
  const voiceChat = VoiceChatService.getInstance();
  voiceChat.cleanup();
  
  // Hide microphone button
  if (micToggleButton) {
    micToggleButton.classList.remove('show');
  }
}

function updateMicButtonUI(enabled: boolean): void {
  if (!micToggleButton) return;
  
  if (enabled) {
    micToggleButton.classList.add('active');
    micToggleButton.classList.remove('muted');
    micToggleButton.innerHTML = '🎤';
    micToggleButton.title = 'Microphone ON (click to mute)';
  } else {
    micToggleButton.classList.remove('active');
    micToggleButton.classList.add('muted');
    micToggleButton.innerHTML = '🎙️';
    micToggleButton.title = 'Microphone OFF (click to enable)';
  }
}

// Initialize
async function init(): Promise<void> {
  // Set canvas size
  canvas.width = 1280;
  canvas.height = 720;

  // Display version
  if (versionBadge) {
    versionBadge.textContent = `v${__APP_VERSION__}`;
  }
  
  // Preload assets on app startup
  await preloadAssets();

  // Load saved player name
  const savedName = localStorage.getItem('cybergrid_playerName');
  if (savedName) {
    playerNameInput.value = savedName;
  }

  // Load settings
  loadSettings();

  // Event listeners
  hostButton.addEventListener('click', handleHost);
  joinButton.addEventListener('click', handleJoinClick);
  connectButton.addEventListener('click', handleConnect);
  backButton.addEventListener('click', handleBack);

  playerNameInput.addEventListener('input', () => {
    localStorage.setItem('cybergrid_playerName', playerNameInput.value);
  });

  // Settings listeners
  settingsButton.addEventListener('click', () => {
    settingsPanel.classList.toggle('hidden');
    if (!settingsPanel.classList.contains('hidden')) {
      AnalyticsService.trackUIOpen('settings');
    }
  });
  
  settingsCloseButton.addEventListener('click', () => {
    loadSettings(); // Reset to saved values
    settingsPanel.classList.add('hidden');
  });
  
  settingsSaveButton.addEventListener('click', saveSettings);
  
  // Credits modal listeners
  creditsButton.addEventListener('click', () => {
    creditsModal.classList.toggle('hidden');
    if (!creditsModal.classList.contains('hidden')) {
      AnalyticsService.trackUIOpen('credits');
    }
  });
  
  creditsCloseButton.addEventListener('click', () => {
    creditsModal.classList.add('hidden');
  });
  
  // How to Play modal listeners
  howToPlayButton.addEventListener('click', () => {
    howToPlayModal.classList.toggle('hidden');
    if (!howToPlayModal.classList.contains('hidden')) {
      AnalyticsService.trackUIOpen('how_to_play');
    }
  });
  
  howToPlayCloseButton.addEventListener('click', () => {
    howToPlayModal.classList.add('hidden');
  });
  
  // Stats modal listeners
  statsButton.addEventListener('click', () => {
    statsModal.classList.toggle('hidden');
    if (!statsModal.classList.contains('hidden')) {
      loadStatsModal();
    }
  });
  
  statsCloseButton.addEventListener('click', () => {
    statsModal.classList.add('hidden');
  });
  
  // Microphone toggle button listener
  micToggleButton.addEventListener('click', async () => {
    uiAudio.playClick();
    const voiceChat = VoiceChatService.getInstance();
    const newState = await voiceChat.toggleMicrophone();
    updateMicButtonUI(newState);
  });
  
  micToggleButton.addEventListener('mouseenter', () => {
    uiAudio.playHover();
  });
  
  // Lobby browser listeners
  findMatchButton.addEventListener('click', () => {
    openLobbyBrowser();
  });
  
  lobbyBrowserCloseButton.addEventListener('click', () => {
    closeLobbyBrowser();
  });
  
  refreshLobbiesButton.addEventListener('click', () => {
    refreshLobbies();
  });
  
  // Make Public toggle - create/remove public lobby when toggled
  lobbyMakePublicCheckbox.addEventListener('change', async () => {
    if (!lobbyState.isHost) return;
    
    lobbyState.isPublic = lobbyMakePublicCheckbox.checked;
    const playerName = playerNameInput.value.trim() || 'Player';
    
    if (lobbyState.isPublic) {
      console.log('[Lobby] Making lobby public...');
      try {
        await LobbyBrowserService.initialize();
        await LobbyBrowserService.createPublicLobby({
          roomCode: lobbyState.roomCode,
          hostId: 'local',
          hostName: playerName,
          gameMode: gameSettings.gameMode as 'deathmatch' | 'spike',
          mapId: gameSettings.mapId,
          maxPlayers: lobbyState.maxPlayers,
        });
        console.log('[Lobby] Lobby is now public');
      } catch (e) {
        console.error('[Lobby] Failed to make lobby public:', e);
      }
    } else {
      console.log('[Lobby] Making lobby private...');
      try {
        await LobbyBrowserService.removeLobby(lobbyState.roomCode);
        console.log('[Lobby] Lobby is now private');
      } catch (e) {
        console.error('[Lobby] Failed to make lobby private:', e);
      }
    }
  });
  
  // Update displayed values when sliders change
  botCountSlider.addEventListener('input', () => {
    botCountValue.textContent = botCountSlider.value;
  });
  
  friendlyBotsSlider.addEventListener('input', () => {
    friendlyBotsValue.textContent = friendlyBotsSlider.value;
  });
  
  // Game mode selection listener - filter maps when mode changes
  gameModeSelect.addEventListener('change', () => {
    populateMapDropdown(gameModeSelect.value);
  });
  
  // Map selection listener - update hint when map changes
  mapSelect.addEventListener('change', updateMapHint);
  
  // Hero selection listener - update ability names when hero changes
  heroSelect.addEventListener('change', updateAbilityNames);
  
  // Ability slot selection listeners - update ability names when slots change
  abilityQSelect.addEventListener('change', updateAbilityNames);
  abilityFSelect.addEventListener('change', updateAbilityNames);

  // Pause menu listeners
  resumeButton.addEventListener('click', resumeGame);
  quitButton.addEventListener('click', quitToMenu);
  
  // Round over panel listeners
  playAgainButton.addEventListener('click', playAgain);
  mainMenuButton.addEventListener('click', () => {
    resetIdleTimer();
    clearIdleTimeout();
    quitToMenu();
  });
  pauseCountdownButton.addEventListener('click', toggleCountdownPause);
  
  // Track interactions on round over panel to reset idle timer
  roundOverPanel.addEventListener('mousemove', resetIdleTimer);
  roundOverPanel.addEventListener('keydown', resetIdleTimer);
  roundOverPanel.addEventListener('click', resetIdleTimer);
  
  // Room code copy button
  copyCodeButton.addEventListener('click', () => {
    const code = roomCodeText.textContent || '';
    navigator.clipboard.writeText(code).then(() => {
      copyCodeButton.textContent = 'Copied!';
      copyCodeButton.classList.add('copied');
      setTimeout(() => {
        copyCodeButton.textContent = 'Copy';
        copyCodeButton.classList.remove('copied');
      }, 2000);
    }).catch(() => {
      // Fallback for older browsers
      alert(`Room Code: ${code}`);
    });
  });
  
  // Lobby listeners
  leaveLobbyButton.addEventListener('click', leaveLobby);
  
  startGameButton.addEventListener('click', () => {
    if (lobbyState.isHost) {
      startGameFromLobby();
    }
  });
  
  // Helper function to copy lobby room code
  const copyLobbyCode = () => {
    const code = lobbyRoomCode.textContent || '';
    navigator.clipboard.writeText(code).then(() => {
      // Visual feedback - just flash the color, don't change text
      lobbyRoomCode.classList.add('copied');
      copyLobbyCodeButton.textContent = '✓ Copied!';
      setTimeout(() => {
        lobbyRoomCode.classList.remove('copied');
        copyLobbyCodeButton.textContent = '📋 Copy Code';
      }, 1500);
    }).catch(() => {
      alert(`Room Code: ${code}`);
    });
  };

  copyLobbyCodeButton.addEventListener('click', copyLobbyCode);
  
  // Click on room code to copy
  lobbyRoomCode.addEventListener('click', copyLobbyCode);
  
  lobbyPlayerCountSelect.addEventListener('change', () => {
    lobbyState.maxPlayers = parseInt(lobbyPlayerCountSelect.value);
    
    // Disable "Make Public" for solo play (1 player)
    if (lobbyState.maxPlayers === 1) {
      lobbyMakePublicCheckbox.disabled = true;
      lobbyMakePublicCheckbox.checked = false;
      // If public lobby exists, remove it
      if (lobbyState.isPublic) {
        LobbyBrowserService.removeLobby(lobbyState.roomCode);
        lobbyState.isPublic = false;
      }
    } else {
      lobbyMakePublicCheckbox.disabled = false;
    }
    
    updateLobbyUI();
  });
  
  // Audio settings listeners - unified function for both panels
  const setupAudioSlider = (
    slider: HTMLInputElement, 
    enabled: HTMLInputElement, 
    valueDisplay: HTMLSpanElement,
    settingKey: string,
    isSettingsPanel: boolean = false
  ) => {
    slider.addEventListener('input', () => {
      const value = parseInt(slider.value);
      valueDisplay.textContent = `${value}%`;
      
      // Update audioSettings storage
      (audioSettings as unknown as Record<string, number | boolean>)[settingKey] = value;
      saveAudioSettings();
      
      // Sync to other panel
      if (isSettingsPanel) {
        // Sync to pause menu panel
        if (settingKey === 'masterVolume') { audioMasterVolume.value = slider.value; audioMasterValue.textContent = `${value}%`; }
        if (settingKey === 'musicVolume') { audioMusicVolume.value = slider.value; audioMusicValue.textContent = `${value}%`; }
        if (settingKey === 'sfxVolume') { audioSfxVolume.value = slider.value; audioSfxValue.textContent = `${value}%`; }
        if (settingKey === 'voiceVolume') { audioVoiceVolume.value = slider.value; audioVoiceValue.textContent = `${value}%`; }
        if (settingKey === 'uiVolume') { audioUiVolume.value = slider.value; audioUiValue.textContent = `${value}%`; }
      } else {
        // Sync to settings panel
        if (settingKey === 'masterVolume') { settingsAudioMasterVolume.value = slider.value; settingsAudioMasterValue.textContent = `${value}%`; }
        if (settingKey === 'musicVolume') { settingsAudioMusicVolume.value = slider.value; settingsAudioMusicValue.textContent = `${value}%`; }
        if (settingKey === 'sfxVolume') { settingsAudioSfxVolume.value = slider.value; settingsAudioSfxValue.textContent = `${value}%`; }
        if (settingKey === 'voiceVolume') { settingsAudioVoiceVolume.value = slider.value; settingsAudioVoiceValue.textContent = `${value}%`; }
        if (settingKey === 'uiVolume') { settingsAudioUiVolume.value = slider.value; settingsAudioUiValue.textContent = `${value}%`; }
      }
      
      // Update game audio if running
      if (game) {
        game.updateAudioSettings({ [settingKey]: value });
      }
      
      // Update standalone UI audio for menu sounds
      if (settingKey === 'uiVolume' || settingKey === 'masterVolume') {
        const masterVol = audioSettings.masterVolume / 100;
        const uiVol = audioSettings.uiVolume / 100;
        uiAudio.setVolume(masterVol * uiVol);
      }

      // Update music player volume
      if (settingKey === 'musicVolume' || settingKey === 'masterVolume') {
        const masterVol = audioSettings.masterVolume / 100;
        const musicVol = audioSettings.musicVolume / 100;
        musicPlayer.setVolume(masterVol * musicVol);
      }
    });
    
    enabled.addEventListener('change', () => {
      const isEnabled = enabled.checked;
      slider.disabled = !isEnabled;
      
      // Update audioSettings storage
      const enabledKey = settingKey.replace('Volume', 'Enabled');
      (audioSettings as unknown as Record<string, number | boolean>)[enabledKey] = isEnabled;
      saveAudioSettings();
      
      // Sync to other panel
      if (isSettingsPanel) {
        if (settingKey === 'masterVolume') { audioMasterEnabled.checked = isEnabled; audioMasterVolume.disabled = !isEnabled; }
        if (settingKey === 'musicVolume') { audioMusicEnabled.checked = isEnabled; audioMusicVolume.disabled = !isEnabled; }
        if (settingKey === 'sfxVolume') { audioSfxEnabled.checked = isEnabled; audioSfxVolume.disabled = !isEnabled; }
        if (settingKey === 'voiceVolume') { audioVoiceEnabled.checked = isEnabled; audioVoiceVolume.disabled = !isEnabled; }
        if (settingKey === 'uiVolume') { audioUiEnabled.checked = isEnabled; audioUiVolume.disabled = !isEnabled; }
      } else {
        if (settingKey === 'masterVolume') { settingsAudioMasterEnabled.checked = isEnabled; settingsAudioMasterVolume.disabled = !isEnabled; }
        if (settingKey === 'musicVolume') { settingsAudioMusicEnabled.checked = isEnabled; settingsAudioMusicVolume.disabled = !isEnabled; }
        if (settingKey === 'sfxVolume') { settingsAudioSfxEnabled.checked = isEnabled; settingsAudioSfxVolume.disabled = !isEnabled; }
        if (settingKey === 'voiceVolume') { settingsAudioVoiceEnabled.checked = isEnabled; settingsAudioVoiceVolume.disabled = !isEnabled; }
        if (settingKey === 'uiVolume') { settingsAudioUiEnabled.checked = isEnabled; settingsAudioUiVolume.disabled = !isEnabled; }
      }
      
      // Update game audio if running
      if (game) {
        game.updateAudioSettings({ [enabledKey]: isEnabled });
      }
      
      // Update standalone UI audio for menu sounds
      if (settingKey === 'uiVolume' || settingKey === 'masterVolume') {
        uiAudio.setEnabled(audioSettings.masterEnabled && audioSettings.uiEnabled);
      }
    });
  };
  
  // Setup pause menu audio sliders
  setupAudioSlider(audioMasterVolume, audioMasterEnabled, audioMasterValue, 'masterVolume');
  setupAudioSlider(audioMusicVolume, audioMusicEnabled, audioMusicValue, 'musicVolume');
  setupAudioSlider(audioSfxVolume, audioSfxEnabled, audioSfxValue, 'sfxVolume');
  setupAudioSlider(audioVoiceVolume, audioVoiceEnabled, audioVoiceValue, 'voiceVolume');
  setupAudioSlider(audioUiVolume, audioUiEnabled, audioUiValue, 'uiVolume');
  
  // Setup settings panel audio sliders
  setupAudioSlider(settingsAudioMasterVolume, settingsAudioMasterEnabled, settingsAudioMasterValue, 'masterVolume', true);
  setupAudioSlider(settingsAudioMusicVolume, settingsAudioMusicEnabled, settingsAudioMusicValue, 'musicVolume', true);
  setupAudioSlider(settingsAudioSfxVolume, settingsAudioSfxEnabled, settingsAudioSfxValue, 'sfxVolume', true);
  setupAudioSlider(settingsAudioVoiceVolume, settingsAudioVoiceEnabled, settingsAudioVoiceValue, 'voiceVolume', true);
  setupAudioSlider(settingsAudioUiVolume, settingsAudioUiEnabled, settingsAudioUiValue, 'uiVolume', true);
  
  // Other pause menu slider listeners
  sensitivitySlider.addEventListener('input', () => {
    sensitivityValue.textContent = (parseFloat(sensitivitySlider.value) / 10).toFixed(1);
  });
  
  fpsCheckbox.addEventListener('change', () => {
    // Update the game's debug/FPS display immediately
    if (game) {
      game.setShowFPS(fpsCheckbox.checked);
    }
  });

  // Load gameplay settings
  loadGameplaySettings();
  
  // Load audio settings
  loadAudioSettings();
  
  // Initialize music player UI
  if (musicPlayerElement && musicHeader && musicPausePlayBtn && musicStopBtn && musicTracks.length > 0) {
    // Music player minimize/maximize toggle
    musicHeader.addEventListener('click', () => {
      uiAudio.playClick();
      musicPlayerElement.classList.toggle('minimized');
    });

    // Pause/Play button
    musicPausePlayBtn.addEventListener('click', async (e) => {
      e.stopPropagation(); // Prevent header click toggle
      uiAudio.playClick();

      if (!musicPlayer.getCurrentTrack()) {
        return; // No track selected
      }

      if (musicPlayer.isPausedState()) {
        await musicPlayer.resume();
        musicPausePlayBtn.textContent = '⏸ Pause';
      } else {
        musicPlayer.pause();
        musicPausePlayBtn.textContent = '▶ Play';
      }
    });

    // Stop button
    musicStopBtn.addEventListener('click', (e) => {
      e.stopPropagation(); // Prevent header click toggle
      uiAudio.playClick();
      musicPlayer.stop();
      musicPausePlayBtn.textContent = '⏸ Pause';
      // Remove playing state from all tracks
      musicTracks.forEach(track => track.classList.remove('playing'));
    });

    // Track selection
    musicTracks.forEach(track => {
      track.addEventListener('click', async () => {
        const trackId = track.dataset.track;
        if (!trackId) return;

        uiAudio.playClick();

        // If clicking the currently playing track, stop it
        if (musicPlayer.getCurrentTrack() === trackId) {
          musicPlayer.stop();
          track.classList.remove('playing');
          return;
        }

        // Play the selected track
        await musicPlayer.play(trackId);
        musicPausePlayBtn.textContent = '⏸ Pause';

        // Update playing state
        musicTracks.forEach(t => t.classList.remove('playing'));
        track.classList.add('playing');
      });
    });
  }

  // Keyboard shortcuts
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Escape') {
      // Close settings panel if open (main menu)
      if (!settingsPanel.classList.contains('hidden')) {
        settingsPanel.classList.add('hidden');
        return;
      }
      
      // Close buy menu if open
      if (!buyMenu.classList.contains('hidden')) {
        closeBuyMenu();
        return;
      }
      
      // In-game pause toggle
      if (game) {
        if (!pauseMenu.classList.contains('hidden')) {
          // Resume game
          resumeGame();
        } else {
          // Pause game
          pauseGame();
        }
      }
    }
    
    // Buy menu toggle (B key)
    if (e.code === 'KeyB' && game && !pauseMenu.classList.contains('hidden') === false) {
      if (!buyMenu.classList.contains('hidden')) {
        closeBuyMenu();
      } else if (pauseMenu.classList.contains('hidden')) {
        openBuyMenu();
      }
    }
    
    // Help panel toggle (H key) - shows hero info card
    if (e.code === 'KeyH' && game && pauseMenu.classList.contains('hidden') && buyMenu.classList.contains('hidden')) {
      game.showHeroInfo();
    }
    
    // Countdown pause toggle (P key) - between rounds
    if (e.code === 'KeyP' && game) {
      game.toggleCountdownPause();
    }
  });

  // Tab focus/blur detection for multiplayer pause sync
  document.addEventListener('visibilitychange', () => {
    if (!game || !gameStarted) return;
    
    if (document.hidden) {
      // Tab is hidden (user switched tabs or minimized)
      game.onTabBlur();
    } else {
      // Tab is visible again
      game.onTabFocus();
    }
  });

  // Also handle window blur/focus for alt-tabbing within same browser
  window.addEventListener('blur', () => {
    if (!game || !gameStarted) return;
    game.onTabBlur();
  });

  window.addEventListener('focus', () => {
    if (!game || !gameStarted) return;
    game.onTabFocus();
  });

  // Save play time when page is about to unload (closing tab, navigating away, etc.)
  // Using both beforeunload and pagehide for maximum browser coverage
  const handlePageUnload = () => {
    if (game) {
      // Use synchronous localStorage save - async Firebase won't complete during unload
      // The saved data will be recovered and synced on next session start
      try {
        game.saveStatsOnUnload();
      } catch (e) {
        console.warn('[Main] Failed to save stats on unload:', e);
      }
    }
  };

  window.addEventListener('beforeunload', handlePageUnload);
  window.addEventListener('pagehide', handlePageUnload);
  
  // Buy menu weapon click handlers
  weaponItems.forEach(item => {
    item.addEventListener('click', () => {
      const weaponId = item.dataset.weapon;
      if (weaponId && game) {
        tryBuyWeapon(weaponId);
      }
    });
  });

  // Reset auto-close timer on mouse movement over buy menu
  buyMenu.addEventListener('mousemove', () => {
    // Only reset if menu is visible
    if (!buyMenu.classList.contains('hidden')) {
      startBuyMenuAutoCloseTimer();
    }
  });

  // Add UI sounds to all buttons
  const addButtonSounds = () => {
    const buttons = document.querySelectorAll('button, .btn, .lobby-player-count-option, .weapon-item');
    buttons.forEach(button => {
      button.addEventListener('mouseenter', () => uiAudio.playHover());
      button.addEventListener('mousedown', () => uiAudio.playClick());
    });
  };
  addButtonSounds();

  console.log('[Main] CyberGrid Assault initialized');
  console.log('[Main] Press HOST to start a local game');
}

async function handleHost(): Promise<void> {
  const playerName = playerNameInput.value.trim() || 'Player';
  
  if (!playerName) {
    alert('Please enter your callsign');
    return;
  }
  
  // Track game start in analytics
  AnalyticsService.trackGameStart('host', playerName);
  
  // Generate room code
  const roomCode = generateRoomCode();
  
  // For single player (1 player setting), go directly to lobby without networking
  const requiredPlayers = parseInt(lobbyPlayerCountSelect.value);
  if (requiredPlayers === 1) {
    showLobby(true, roomCode, playerName);
    return;
  }
  
  // For multiplayer, connect to Firebase and create room in lobby
  connectionUI.classList.add('hidden');
  
  try {
    // Create network manager for lobby
    lobbyNetwork = new NetworkManager({ isHost: true, useFirestore: true });
    await lobbyNetwork.connectWithFirebase(FIREBASE_CONFIG);
    
    // Set up listeners for peers joining
    lobbyNetwork.on('peerConnected', ({ peerId }) => {
      console.log(`[Lobby] Peer connected (WebRTC ready): ${peerId}`);
      // Add player to lobby with placeholder name (will be updated via PlayerJoin packet)
      lobbyState.players.push({
        id: peerId,
        name: `Connecting...`,
        isHost: false,
      });
      updateLobbyUI();
    });
    
    lobbyNetwork.on('peerDisconnected', ({ peerId }) => {
      console.log(`[Lobby Host] Peer disconnected: ${peerId}`);
      console.log(`[Lobby Host] Players BEFORE filter:`, JSON.stringify(lobbyState.players));
      lobbyState.players = lobbyState.players.filter(p => p.id !== peerId);
      console.log(`[Lobby Host] Players AFTER filter:`, JSON.stringify(lobbyState.players));
      updateLobbyUI();
    });
    
    // Listen for packets - handle PlayerJoin to get real names
    lobbyNetwork.on('packet', ({ peerId, packet }) => {
      console.log(`[Lobby Host] Received packet from ${peerId}:`, packet.type);
      
      // Handle PlayerJoin packet to get real player name
      if (packet.type === PacketType.PlayerJoin) {
        const data = packet.data as { name: string };
        console.log(`[Lobby Host] Player ${peerId} name: ${data.name}`);
        
        // Update player name in lobby
        const player = lobbyState.players.find(p => p.id === peerId);
        if (player) {
          player.name = data.name;
          updateLobbyUI();
        }
      }
    });
    
    // Create room in Firebase
    await lobbyNetwork.createRoom();
    const actualRoomCode = lobbyNetwork.getRoomCode();
    
    console.log(`[Main] Room created in Firebase: ${actualRoomCode}`);
    
    // Show lobby with the Firebase room code
    showLobby(true, actualRoomCode, playerName);
    
  } catch (e) {
    console.error('[Main] Failed to create room:', e);
    alert('Failed to create game room. Check console for details.');
    connectionUI.classList.remove('hidden');
    lobbyNetwork = null;
  }
}

function handleJoinClick(): void {
  // Show room code input and join buttons (connect + back)
  hostButton.classList.add('hidden');
  joinButton.classList.add('hidden');
  roomCodeInput.classList.remove('hidden');
  joinButtons.classList.remove('hidden');
  roomCodeInput.focus();
}

function handleBack(): void {
  // Go back to main menu options
  roomCodeInput.classList.add('hidden');
  joinButtons.classList.add('hidden');
  roomCodeInput.value = '';
  hostButton.classList.remove('hidden');
  joinButton.classList.remove('hidden');
}

async function handleConnect(): Promise<void> {
  const roomCode = roomCodeInput.value.trim().toUpperCase();
  if (!roomCode) {
    alert('Please enter a room code');
    return;
  }

  const playerName = playerNameInput.value.trim() || 'Player';
  
  if (!playerName) {
    alert('Please enter your callsign');
    return;
  }

  // Track game start in analytics
  AnalyticsService.trackGameStart('join', playerName);

  // Hide connection UI
  connectionUI.classList.add('hidden');
  roomCodeInput.classList.add('hidden');
  joinButtons.classList.add('hidden');
  
  try {
    // Create network manager for lobby
    lobbyNetwork = new NetworkManager({ isHost: false, useFirestore: true });
    await lobbyNetwork.connectWithFirebase(FIREBASE_CONFIG);
    
    // Set up listeners for peers
    lobbyNetwork.on('peerConnected', ({ peerId }) => {
      console.log(`[Lobby Joiner] Peer connected: ${peerId}`);
      // This is the host connecting to us
      lobbyState.players.push({
        id: peerId,
        name: `Host`,
        isHost: true,
      });
      updateLobbyUI();
      
      // Send our name to the host
      console.log(`[Lobby Joiner] Sending our name to host: ${playerName}`);
      lobbyNetwork?.broadcast({
        type: PacketType.PlayerJoin,
        timestamp: Date.now(),
        senderId: lobbyNetwork.getLocalPeerId(),
        data: { name: playerName },
      });
    });
    
    lobbyNetwork.on('peerDisconnected', ({ peerId }) => {
      console.log(`[Lobby] Peer disconnected: ${peerId}`);
      lobbyState.players = lobbyState.players.filter(p => p.id !== peerId);
      updateLobbyUI();
    });
    
    // Listen for GameStart packet from host
    lobbyNetwork.on('packet', ({ peerId, packet }) => {
      console.log(`[Lobby Joiner] Received packet from ${peerId}:`, packet.type);
      if (packet.type === PacketType.GameStart) {
        console.log(`[Lobby] Host started the game!`);
        // Host started the game - apply host's settings and start
        const data = packet.data as { 
          roomCode: string; 
          totalRounds: number;
          mapId?: string;
          gameMode?: string;
        };
        if (data.totalRounds) {
          gameSettings.totalRounds = data.totalRounds;
        }
        if (data.mapId) {
          gameSettings.mapId = data.mapId;
          console.log(`[Lobby Joiner] Using host's map: ${data.mapId}`);
        }
        if (data.gameMode) {
          gameSettings.gameMode = data.gameMode as 'deathmatch' | 'spike';
          console.log(`[Lobby Joiner] Using host's game mode: ${data.gameMode}`);
        }
        startGameFromLobby();
      }
    });
    
    // Join the room in Firebase
    await lobbyNetwork.joinRoom(roomCode);
    
    console.log(`[Main] Joined room in Firebase: ${roomCode}`);
    
    // Show lobby as non-host
    showLobby(false, roomCode, playerName);
    
  } catch (e) {
    console.error('[Main] Failed to join room:', e);
    alert('Failed to join game. Make sure the room code is correct and the host is online.');
    
    // Show connection UI again
    connectionUI.classList.remove('hidden');
    hostButton.classList.remove('hidden');
    joinButton.classList.remove('hidden');
    lobbyNetwork = null;
  }
}

// Weapon slot tooltip system
const weaponTooltip = document.getElementById('weapon-tooltip') as HTMLDivElement;
const weaponNames: Record<number, string> = {
  1: 'Burst Pistol',
  2: 'Neo SMG-9',
  3: 'Pulse Rifle',
  4: 'Scatter Shot',
  5: 'Rail Sniper',
  6: 'Shard Launcher',
  7: 'Nano Repeater',
  8: 'Arc Welder',
  9: 'Phase Repeater',
  10: 'Plasma Cannon'
};

// Show weapon name on key press
document.addEventListener('keydown', (e) => {
  if (!game || !game.isRunning()) return;
  
  // Map key to slot number
  let slotNumber = 0;
  if (e.code === 'Digit1') slotNumber = 1;
  else if (e.code === 'Digit2') slotNumber = 2;
  else if (e.code === 'Digit3') slotNumber = 3;
  else if (e.code === 'Digit4') slotNumber = 4;
  else if (e.code === 'Digit5') slotNumber = 5;
  else if (e.code === 'Digit6') slotNumber = 6;
  else if (e.code === 'Digit7') slotNumber = 7;
  else if (e.code === 'Digit8') slotNumber = 8;
  else if (e.code === 'Digit9') slotNumber = 9;
  else if (e.code === 'Digit0') slotNumber = 10;
  
  if (slotNumber > 0) {
    const weaponName = weaponNames[slotNumber];
    if (weaponName) {
      weaponTooltip.textContent = weaponName;
      weaponTooltip.classList.add('visible');
      
      // Hide after 1.5 seconds
      setTimeout(() => {
        weaponTooltip.classList.remove('visible');
      }, 1500);
    }
  }
});

// Start
init().catch(console.error);
